/**
 * Environment-driven dashboard configuration.
 *
 * react-ui tenant packs are static data, but dashboard URLs are
 * deployment-specific (NGV points at the platform API, LSEG at a separate
 * dashboard application, either overridable by environment variable), so they
 * cannot be baked into a published pack. Pathway therefore owns the
 * dashboard configuration locally, keyed by the tenant's asset folder
 * (`RuntimeBrand.folder`). This replaces the per-brand `dashboard` block that
 * react-auth v3 carried and the `customBrands` injection that lived in
 * `main.tsx`.
 */

import type { DashboardConfig } from "@webuild-ai/react-ui/types";
import type { TenantFolder } from "./tenants";

import { isTenantFolder } from "./tenants";
import { siteConfig } from "./site";

/**
 * Dashboard configuration for a tenant, or `undefined` when the tenant has no
 * dashboard. NGV, LSEG and WeBuild AI each ship one; NGET deliberately has none
 * (parity with the v3 wiring, which stripped NGV's inherited dashboard for
 * NGET). Takes `RuntimeBrand.folder` (a plain `string`, since it must also
 * carry the neutral fallback brand's `"fallback"` value); the switch below is
 * exhaustive over `TenantFolder` so a newly added tenant with no case here is
 * a compile error rather than a silent no-dashboard result.
 */
export function getDashboardConfig(folder: string): DashboardConfig | undefined {
  if (!isTenantFolder(folder)) {
    return undefined;
  }

  return dashboardConfigForFolder(folder);
}

function dashboardConfigForFolder(folder: TenantFolder): DashboardConfig | undefined {
  switch (folder) {
    case "ngv": {
      const url =
        (import.meta.env.VITE_NGV_DASHBOARD_URL as string | undefined) ||
        `${siteConfig().platformAPIURL}/ncrmweb`;

      return {
        type: "iframe",
        url,
        title: "ngv.dashboard.title",
        subtitle: "ngv.dashboard.subtitle",
        showInSideBar: true,
        urlSegment: "ncrm",
      };
    }

    case "webuild": {
      // WeBuild AI ships a self-contained marketing dashboard served from the app
      // origin (public/brands/webuild/dashboard.html). It is the landing page,
      // not a sidebar entry: with showInSideBar left false, IndexPage redirects
      // "/" to "/dashboard" (the default urlSegment). VITE_DASHBOARD_URL overrides
      // the local file for environments that host a bespoke dashboard, mirroring
      // NGV's VITE_NGV_DASHBOARD_URL override.
      const url =
        (import.meta.env.VITE_DASHBOARD_URL as string | undefined) ||
        "/brands/webuild/dashboard.html";

      return {
        type: "iframe",
        url,
      };
    }

    case "lseg": {
      // Defaults to the dashboard bundled at public/brands/lseg/dashboard.html.
      // Deployments hosting the separate dashboard application set
      // VITE_LSEG_DASHBOARD_URL, mirroring NGV's VITE_NGV_DASHBOARD_URL. The
      // deployed AWS bundle is built in Vite's `production` mode, so that value
      // comes from frontend-aws.yml rather than from .env.dev-aws-lseg.
      const url =
        (import.meta.env.VITE_LSEG_DASHBOARD_URL as string | undefined) ||
        "/brands/lseg/dashboard.html";

      return {
        type: "iframe",
        url,
        title: "lseg.dashboard.title",
        subtitle: "lseg.dashboard.subtitle",
        showInSideBar: true,
        urlSegment: "risk",
        // The default sandbox omits allow-downloads, which makes a sandboxed
        // frame drop download requests silently. LSEG's dashboard offers the
        // published briefing PDFs, so it needs the token. Scoped to this tenant
        // rather than added to DEFAULT_SANDBOX, because that default also covers
        // third-party vendor dashboards where the narrower grant is right.
        sandbox: "allow-scripts allow-same-origin allow-popups allow-forms allow-downloads",
      };
    }

    case "nget":
      return undefined;

    default: {
      const _exhaustive: never = folder;

      return _exhaustive;
    }
  }
}

/**
 * Origins a dashboard iframe is permitted to load from, for one tenant.
 *
 * This validates the user-controlled `?dashboardUrl=` override, and
 * `IframeDashboard` then posts the bearer token to the origin derived from it,
 * so the list must stay scoped to `folder`. Reading the dashboard environment
 * variables directly instead trusted every tenant's dashboard host on every
 * tenant, which matters because one AWS bundle serves the WeBuild dev, LSEG risk
 * dev and WeBuild production buckets.
 *
 * @param folder - The active brand's asset folder (`RuntimeBrand.folder`).
 */
export function getAllowedDashboardOrigins(folder: string): string[] {
  const origins = new Set<string>();
  const add = (value: string | undefined) => {
    if (!value) return;
    try {
      origins.add(new URL(value, window.location.origin).origin);
    } catch {
      // Ignore unparseable configuration values.
    }
  };

  add(window.location.origin);
  add(siteConfig().platformAPIURL);
  // Resolved rather than read from the environment, so the override and the
  // bundled default are both covered and no other tenant's host can leak in.
  add(getDashboardConfig(folder)?.url);

  return [...origins];
}

/**
 * Distinct dashboard URL segments across every tenant pathway serves. Used to
 * generate dashboard routes, replacing react-auth's `getDashboardUrlSegments`.
 * Kept in sync with `tenantFolders` in `./tenants`.
 */
export function dashboardUrlSegments(folders: readonly string[]): string[] {
  const segments = new Set<string>();

  for (const folder of folders) {
    const segment = getDashboardConfig(folder)?.urlSegment;

    if (segment) {
      segments.add(segment);
    }
  }

  return [...segments];
}
